import React, { useEffect } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../../seo";

const steps = [
  {
    label: "Step 1",
    title: "Upload Your FAQs",
    description:
      "Import your knowledge base via CSV or add entries one by one in the portal. Our AI instantly learns your products, services, and policies.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>
    ),
  },
  {
    label: "Step 2",
    title: "Customize Your Bot",
    description:
      "Set your brand colors, logo, welcome message, and AI persona name. No design or coding skills required — changes preview live.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
  },
  {
    label: "Step 3",
    title: "Embed & Go Live",
    description:
      "Copy one script tag into your website — or let us do it. Works instantly on any platform: Shopify, WordPress, Wix, or custom HTML.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
];

const extras = [
  {
    title: "Business Hours & Auto-Reply",
    description:
      "Set your operating hours and configure auto-reply messages for outside hours. Customers always know when to expect a human.",
  },
  {
    title: "Canned Responses",
    description:
      "Save your team's most common replies as shortcuts. Agents can insert pre-written answers with one click — faster support, consistent tone.",
  },
  {
    title: "Real-Time Notifications",
    description:
      "Get sound alerts, desktop notifications, and tab title flashing when new chats arrive. Never miss a customer again.",
  },
];

export function NoCodePage() {
  useEffect(() => {
    document.title = "No-Code Chatbot Setup — Onyx AI";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Set up your AI chatbot in 3 steps — no coding required. Upload FAQs, customize your bot, and embed one script tag. Live in under 5 minutes."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/features/no-code");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Features", path: "/features" },
      { name: "No-Code Setup", path: "/features/no-code" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <a href="/features" className="hover:text-[#a078d0] transition-colors">Features</a>
            <span>/</span>
            <span className="text-[#8a7a9a]">No-Code Setup</span>
          </nav>

          {/* Hero */}
          <div className="mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              No-Code Setup
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Go Live in 5 Minutes — No Developers Needed
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl">
              Anyone on your team can build and launch an AI chatbot. No code, no technical expertise — just your business knowledge and a few clicks.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/signup"
                className="inline-block px-7 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
              >
                Try It Free
              </a>
              <a
                href="/features"
                className="inline-block px-7 py-3 border border-[#2a2040] hover:border-[#a078d0] text-[#8a7a9a] hover:text-white font-medium rounded-lg transition-colors"
              >
                All Features
              </a>
            </div>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {steps.map((step, i) => (
              <div key={step.label} className="p-6 rounded-xl onyx-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0]">
                    {step.icon}
                  </div>
                  <span className="text-xs font-semibold text-[#a078d0] uppercase tracking-wider">
                    {step.label}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Extra features */}
          <h2 className="text-2xl font-bold text-white mb-6">More No-Code Tools</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {extras.map((item) => (
              <div key={item.title} className="p-5 rounded-xl onyx-card">
                <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
